import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Sparkles, Crown, Trophy, Apple, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

const API = process.env.REACT_APP_BACKEND_URL;

// Rarity colors and styles
const RARITY_STYLES = {
  common: { bg: 'bg-gray-500/20', border: 'border-gray-400', text: 'text-gray-300', glow: '' },
  uncommon: { bg: 'bg-green-500/20', border: 'border-green-400', text: 'text-green-300', glow: '' },
  rare: { bg: 'bg-blue-500/20', border: 'border-blue-400', text: 'text-blue-300', glow: 'shadow-[0_0_10px_rgba(59,130,246,0.3)]' },
  epic: { bg: 'bg-purple-500/20', border: 'border-purple-400', text: 'text-purple-300', glow: 'shadow-[0_0_15px_rgba(168,85,247,0.4)]' },
  legendary: { bg: 'bg-yellow-500/20', border: 'border-yellow-400', text: 'text-yellow-300', glow: 'shadow-[0_0_20px_rgba(234,179,8,0.5)]' }
};

// Item card component - Only shows collected items
function ItemCard({ item, onUse, onEquip, onUnequip, isEquipped }) {
  const rarity = RARITY_STYLES[item.rarity] || RARITY_STYLES.common;
  const owned = item.owned || 0;
  const isEquippable = item.equippable || item.slot;
  
  // Don't render if not owned
  if (owned <= 0) return null;
  
  return (
    <div 
      className={`relative p-3 rounded-xl border-2 ${rarity.border} ${rarity.bg} ${rarity.glow} 
        transition-all duration-300 hover:scale-105 hover:brightness-110
        ${isEquipped ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-gray-900' : ''}`}
    >
      {/* Rarity indicator */}
      <div className={`absolute -top-1 -right-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${rarity.bg} ${rarity.text} border ${rarity.border}`}>
        {item.rarity}
      </div>
      
      {/* Equipped badge */}
      {isEquipped && (
        <div className="absolute -top-1 -left-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-green-500 text-white">
          Equipped
        </div>
      )}
      
      {/* Item icon - image or emoji */}
      <div className="flex justify-center items-center h-12 mb-2">
        {item.image ? (
          <img 
            src={item.image} 
            alt={item.name} 
            className="w-12 h-12 object-contain animate-pulse" 
            style={{ animationDuration: '3s', mixBlendMode: 'multiply', filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.3))' }} 
          />
        ) : (
          <span className="text-4xl animate-pulse" style={{ animationDuration: '3s' }}>{item.emoji}</span>
        )}
      </div>
      
      {/* Item name */}
      <h4 className="text-sm font-bold text-white text-center truncate mb-1">{item.name}</h4>
      
      {/* Description - improved readability */}
      <p className="text-xs text-white/90 text-center line-clamp-2 mb-2 font-medium">{item.description}</p>
      
      {/* Slot indicator for equippable items */}
      {isEquippable && item.slot && (
        <div className="text-center mb-1">
          <span className="text-[10px] text-cyan-400 uppercase">Slot: {item.slot}</span>
        </div>
      )}
      
      {/* Owned count */}
      <div className="flex items-center justify-center">
        <div className={`px-3 py-1 rounded-full text-xs font-bold bg-green-500/30 text-green-300`}>
          x{owned}
        </div>
      </div>
      
      {/* Action buttons */}
      {isEquippable ? (
        isEquipped ? (
          <button 
            onClick={() => onUnequip(item)}
            className="mt-2 w-full py-1.5 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg text-xs font-bold text-white hover:brightness-110 transition-all flex items-center justify-center gap-1"
          >
            <Zap className="w-3 h-3" /> Unequip
          </button>
        ) : (
          <button 
            onClick={() => onEquip(item)}
            className="mt-2 w-full py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg text-xs font-bold text-white hover:brightness-110 transition-all flex items-center justify-center gap-1"
          >
            <Zap className="w-3 h-3" /> Equip
          </button>
        )
      ) : onUse && (
        <button 
          onClick={() => onUse(item)}
          className="mt-2 w-full py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-xs font-bold text-white hover:brightness-110 transition-all flex items-center justify-center gap-1"
        >
          <Zap className="w-3 h-3" /> Use Item
        </button>
      )}
    </div>
  );
}

// Category section component
function CategorySection({ category, data, onUseItem, onEquipItem, onUnequipItem, equippedItems }) {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Only count and show owned items
  const ownedItems = data.items.filter(item => item.owned > 0);
  const totalOwned = ownedItems.length;
  
  // Check if an item is equipped
  const isItemEquipped = (item) => {
    // First check if backend already told us it's equipped
    if (item.is_equipped) return true;
    // Fall back to checking equipped_items object
    if (!equippedItems) return false;
    const slot = item.slot || 'accessory';
    return equippedItems[slot] === item.item_id;
  };
  
  // Don't show category if no items collected
  if (totalOwned === 0) return null;
  
  return (
    <div className="mb-6">
      {/* Category header */}
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all mb-3"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{data.emoji}</span>
          <div className="text-left">
            <h3 className="font-bold text-white">{data.name}</h3>
            {data.levels && <span className="text-xs text-gray-400">Levels {data.levels}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">{totalOwned} collected</span>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </button>
      
      {/* Items grid - only shows owned items */}
      {isExpanded && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {ownedItems.map((item) => (
            <ItemCard 
              key={item.item_id} 
              item={item} 
              onUse={onUseItem}
              onEquip={onEquipItem}
              onUnequip={onUnequipItem}
              isEquipped={isItemEquipped(item)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function InventoryPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [useResult, setUseResult] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Wait for auth to be ready before fetching
    if (!authLoading && isAuthenticated) {
      fetchInventory();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
      setError('Not logged in');
    }
  }, [authLoading, isAuthenticated]);
  
  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('fruitCrush_token');
      console.log('[Inventory] Fetching with token:', !!token);
      
      if (!token) {
        setError('Not logged in');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API}/api/inventory/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('[Inventory] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Inventory] Data received:', Object.keys(data));
        setInventory(data);
      } else {
        const text = await response.text();
        console.error('[Inventory] Error response:', text);
        setError(`Failed to load inventory: ${response.status}`);
      }
    } catch (error) {
      console.error('[Inventory] Error fetching inventory:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleUseItem = async (item) => {
    try {
      const token = localStorage.getItem('fruitCrush_token');
      const endpoint = item.effect ? '/api/vip/use-item' : '/api/inventory/use-item';
      
      const response = await fetch(`${API}${endpoint}?item_id=${item.item_id}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ item_id: item.item_id })
      });
      
      if (response.ok) {
        const result = await response.json();
        setUseResult({
          success: true,
          item: item,
          message: `Used ${item.name}!`
        });
        // Refresh inventory
        fetchInventory();
        
        // Clear result after 3 seconds
        setTimeout(() => setUseResult(null), 3000);
      }
    } catch (error) {
      console.error('Error using item:', error);
      setUseResult({
        success: false,
        message: 'Failed to use item'
      });
    }
  };
  
  const handleEquipItem = async (item) => {
    try {
      const token = localStorage.getItem('fruitCrush_token');
      
      // Use different endpoint for equipment items (like God Sword)
      const isEquipment = item.slot === 'weapon';
      const endpoint = isEquipment 
        ? `${API}/api/equipment/equip`
        : `${API}/api/inventory/equip?item_id=${item.item_id}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: isEquipment ? JSON.stringify({ item_id: item.item_id }) : undefined
      });
      
      if (response.ok) {
        const result = await response.json();
        setUseResult({
          success: true,
          item: item,
          message: `Equipped ${item.name}!`
        });
        // Refresh inventory
        fetchInventory();
        setTimeout(() => setUseResult(null), 3000);
      } else {
        const errorData = await response.json();
        setUseResult({
          success: false,
          message: errorData.detail || 'Failed to equip item'
        });
        setTimeout(() => setUseResult(null), 3000);
      }
    } catch (error) {
      console.error('Error equipping item:', error);
      setUseResult({
        success: false,
        message: 'Failed to equip item'
      });
      setTimeout(() => setUseResult(null), 3000);
    }
  };
  
  const handleUnequipItem = async (item) => {
    try {
      const token = localStorage.getItem('fruitCrush_token');
      const slot = item.slot || 'accessory';
      
      // Use different endpoint for equipment items (like God Sword)
      const isEquipment = item.slot === 'weapon';
      const endpoint = isEquipment 
        ? `${API}/api/equipment/unequip`
        : `${API}/api/inventory/unequip?slot=${slot}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: isEquipment ? JSON.stringify({ item_id: item.item_id }) : undefined
      });
      
      if (response.ok) {
        const result = await response.json();
        setUseResult({
          success: true,
          item: item,
          message: `Unequipped ${item.name}!`
        });
        // Refresh inventory
        fetchInventory();
        setTimeout(() => setUseResult(null), 3000);
      }
    } catch (error) {
      console.error('Error unequipping item:', error);
      setUseResult({
        success: false,
        message: 'Failed to unequip item'
      });
      setTimeout(() => setUseResult(null), 3000);
    }
  };
  
  const tabs = [
    { id: 'all', label: 'All Items', icon: Package },
    { id: 'equipment', label: 'Equipment', icon: Zap },
    { id: 'power', label: 'Power-ups', icon: Sparkles },
    { id: 'vip', label: 'VIP Items', icon: Crown },
    { id: 'achievements', label: 'Achievements', icon: Trophy },
    { id: 'fruits', label: 'Fruits', icon: Apple }
  ];
  
  const getFilteredCategories = () => {
    if (!inventory?.categories) return {};
    
    const categories = inventory.categories;
    
    switch (activeTab) {
      case 'equipment':
        return { equipment: categories.equipment };
      case 'power':
        const { fruits, achievements, vip, equipment, ...powerCategories } = categories;
        return powerCategories;
      case 'vip':
        return { vip: categories.vip };
      case 'achievements':
        return { achievements: categories.achievements };
      case 'fruits':
        return { fruits: categories.fruits };
      default:
        return categories;
    }
  };
  
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-purple-400 animate-bounce mx-auto mb-4" />
          <p className="text-white">Loading inventory...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-white mb-4">{error}</p>
          <button 
            onClick={() => { setError(null); setLoading(true); fetchInventory(); }}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            Retry
          </button>
          <button 
            onClick={() => navigate('/levels')}
            className="ml-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            Back to Game
          </button>
        </div>
      </div>
    );
  }
  
  const filteredCategories = getFilteredCategories();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900">
      {/* Use result notification */}
      {useResult && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl ${useResult.success ? 'bg-green-500' : 'bg-red-500'} text-white font-bold shadow-2xl animate-bounce`}>
          <div className="flex items-center gap-2">
            {useResult.item && <span className="text-2xl">{useResult.item.emoji}</span>}
            <span>{useResult.message}</span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="sticky top-0 z-40 bg-gray-900/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/levels')}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Package className="w-6 h-6 text-purple-400" />
                  Inventory
                </h1>
                {inventory?.stats && (
                  <p className="text-sm text-gray-400">
                    {inventory.stats.unique_items} unique items • {inventory.stats.total_items} total
                  </p>
                )}
              </div>
            </div>
            
            {/* Rarity summary */}
            {inventory?.stats?.rarity_counts && (
              <div className="hidden md:flex items-center gap-2">
                {Object.entries(inventory.stats.rarity_counts).map(([rarity, count]) => (
                  count > 0 && (
                    <div key={rarity} className={`px-2 py-1 rounded-full text-xs font-bold ${RARITY_STYLES[rarity]?.bg} ${RARITY_STYLES[rarity]?.text} border ${RARITY_STYLES[rarity]?.border}`}>
                      {count} {rarity}
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all
                  ${activeTab === tab.id 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {Object.entries(filteredCategories).map(([key, data]) => (
          data.items.length > 0 && (
            <CategorySection 
              key={key} 
              category={key} 
              data={data} 
              onUseItem={handleUseItem}
              onEquipItem={handleEquipItem}
              onUnequipItem={handleUnequipItem}
              equippedItems={inventory?.equipped_items}
            />
          )
        ))}
        
        {/* Empty state */}
        {Object.keys(filteredCategories).length === 0 && (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No items in this category</p>
          </div>
        )}
      </div>
    </div>
  );
}
